import { gql, useMutation, useQuery } from '@apollo/client';
import * as React from 'react';

export type MentorshipMemberRole = 'mentor' | 'mentee';

export type MentorshipContext = {
  mentorApplied: boolean;
  menteeApplied: boolean;
  mentorApproved: boolean;
  menteeApproved: boolean;
  mentorOnboarded: boolean;
  menteeOnboarded: boolean;
  selectedRole?: MentorshipMemberRole | null;
};

export const MY_MENTORSHIP_CONTEXT = gql`
  query MyMentorshipContext {
    myMentorshipContext {
      mentorApplied
      menteeApplied
      mentorApproved
      menteeApproved
      mentorOnboarded
      menteeOnboarded
      selectedRole
    }
  }
`;

const SELECT_MENTORSHIP_ROLE = gql`
  mutation SelectMentorshipRole($role: String!) {
    selectMentorshipRole(role: $role) {
      mentorApplied
      menteeApplied
      mentorApproved
      menteeApproved
      mentorOnboarded
      menteeOnboarded
      selectedRole
    }
  }
`;

export const useMentorshipContext = (enabled = true) => {
  const query = useQuery<{ myMentorshipContext: MentorshipContext }>(
    MY_MENTORSHIP_CONTEXT,
    { fetchPolicy: 'cache-and-network', skip: !enabled }
  );
  const [selectRoleMutation, selectRoleState] = useMutation<
    { selectMentorshipRole: MentorshipContext },
    { role: MentorshipMemberRole }
  >(SELECT_MENTORSHIP_ROLE, {
    refetchQueries: [{ query: MY_MENTORSHIP_CONTEXT }],
    awaitRefetchQueries: true,
  });

  const selectRole = React.useCallback(
    async (role: MentorshipMemberRole) => {
      const result = await selectRoleMutation({ variables: { role } });
      return result.data?.selectMentorshipRole;
    },
    [selectRoleMutation]
  );

  return {
    ...query,
    context: query.data?.myMentorshipContext,
    selectRole,
    selectingRole: selectRoleState.loading,
  };
};
